import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type UIEvent,
} from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { Message } from '@flock-types/chat.types';
import { auth } from '@/config/firebase';
import { formatDayDivider } from '@/lib/chatTime';
import MessageRow from './MessageRow';

const GROUPING_WINDOW_MS = 5 * 60 * 1000;
const AT_BOTTOM_THRESHOLD_PX = 80;
const LOAD_MORE_THRESHOLD_PX = 120;

function createdAtMillis(message: Message): number {
  const ts: any = message.createdAt;
  if (!ts) return Date.now(); // pending serverTimestamp → treat as "now"
  if (typeof ts === 'object' && ts.toMillis) return ts.toMillis();
  if (typeof ts === 'object' && ts.toDate) return ts.toDate().getTime();
  const ms = new Date(ts).getTime();
  return Number.isNaN(ms) ? Date.now() : ms;
}

type ListItem =
  | { kind: 'message'; message: Message; grouped: boolean; key: string }
  | { kind: 'day'; label: string; key: string }
  | { kind: 'unread'; key: string };

interface Props {
  /** Messages newest-first (as returned by useGroupChat). */
  messages: Message[];
  hasMore: boolean;
  loadMore: () => Promise<void> | void;
  /** Number of unread messages at conversation-open time (frozen) — places the divider. */
  initialUnreadCount?: number;
  onReplyClick?: (messageId: string) => void;
  onToggleReaction?: (message: Message, emoji: string, reacted: boolean) => void;
  renderRichContent?: (message: Message, own: boolean) => React.ReactNode;
  onOpenActions?: (message: Message) => void;
  /** Fresh avatar URL resolver (stored message photo URLs expire). */
  resolveAvatar?: (message: Message) => string | null | undefined;
  /** Message ID to scroll to (search context jump). */
  scrollToMessageId?: string | null;
}

/**
 * Plain scrolling message list (no virtualization).
 *
 * Deliberate choice: react-virtuoso's reverse-list initialization wedged
 * (hidden item list stuck mid initial-scroll) and chat pages are capped at
 * 50 messages per load — plain DOM is simpler and reliable at church scale.
 * Upward pagination preserves the scroll anchor manually.
 */
export default function MessageList({
  messages,
  hasMore,
  loadMore,
  initialUnreadCount = 0,
  onReplyClick,
  onToggleReaction,
  renderRichContent,
  onOpenActions,
  resolveAvatar,
  scrollToMessageId,
}: Props) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [atBottom, setAtBottom] = useState(true);
  const atBottomRef = useRef(true);
  const [newWhileAway, setNewWhileAway] = useState(0);
  const loadingMoreRef = useRef(false);
  const didInitialScrollRef = useRef(false);
  const prevLatestIdRef = useRef<string | null>(null);
  const prevOldestIdRef = useRef<string | null>(null);
  const uid = auth.currentUser?.uid;

  // Freeze the unread divider position on first non-empty render
  const unreadAnchorRef = useRef<string | null>(null);
  const unreadFrozenRef = useRef(false);
  if (!unreadFrozenRef.current && messages.length > 0) {
    unreadFrozenRef.current = true;
    if (initialUnreadCount > 0 && initialUnreadCount < messages.length) {
      unreadAnchorRef.current = messages[initialUnreadCount - 1].id;
    }
  }

  // Build display list oldest→newest with day dividers + sender grouping
  const items = useMemo((): ListItem[] => {
    const asc = [...messages].reverse();
    const out: ListItem[] = [];
    let prev: Message | null = null;
    let prevDay = '';
    for (const m of asc) {
      const date = new Date(createdAtMillis(m));
      const day = date.toDateString();
      if (day !== prevDay) {
        out.push({ kind: 'day', label: formatDayDivider(date), key: `day-${day}` });
        prevDay = day;
        prev = null;
      }
      if (unreadAnchorRef.current && m.id === unreadAnchorRef.current) {
        out.push({ kind: 'unread', key: 'unread-divider' });
        prev = null;
      }
      const grouped = Boolean(
        prev &&
          prev.userId === m.userId &&
          createdAtMillis(m) - createdAtMillis(prev) < GROUPING_WINDOW_MS
      );
      out.push({ kind: 'message', message: m, grouped, key: m.id });
      prev = m;
    }
    return out;
  }, [messages]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    const el = scrollerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  // Initial render: land at the bottom (or at the unread divider when present)
  useLayoutEffect(() => {
    if (didInitialScrollRef.current || items.length === 0) return;
    didInitialScrollRef.current = true;
    const el = scrollerRef.current;
    if (!el) return;
    if (unreadAnchorRef.current) {
      const divider = el.querySelector('[data-unread-divider]');
      if (divider) {
        (divider as HTMLElement).scrollIntoView({ block: 'center' });
        prevLatestIdRef.current = messages[0]?.id ?? null;
        prevOldestIdRef.current = messages[messages.length - 1]?.id ?? null;
        return;
      }
    }
    el.scrollTop = el.scrollHeight;
    prevLatestIdRef.current = messages[0]?.id ?? null;
    prevOldestIdRef.current = messages[messages.length - 1]?.id ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length]);

  /**
   * Message changes after initial render:
   * - older messages prepended (loadMore) → preserve the visual anchor
   * - new message appended → follow output if at bottom, else count it
   */
  const prevScrollHeightRef = useRef(0);
  useLayoutEffect(() => {
    if (!didInitialScrollRef.current) return;
    const el = scrollerRef.current;
    if (!el || messages.length === 0) return;

    const latestId = messages[0]?.id ?? null;
    const oldestId = messages[messages.length - 1]?.id ?? null;

    if (oldestId !== prevOldestIdRef.current && prevScrollHeightRef.current > 0) {
      // Prepend: keep the previously-visible content in place
      el.scrollTop += el.scrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }

    if (latestId && latestId !== prevLatestIdRef.current) {
      if (atBottomRef.current) {
        scrollToBottom('smooth');
      } else {
        setNewWhileAway((c) => c + 1);
      }
    }

    prevLatestIdRef.current = latestId;
    prevOldestIdRef.current = oldestId;
  }, [messages, scrollToBottom]);

  const handleScroll = useCallback(
    async (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const isAtBottom =
        el.scrollHeight - el.scrollTop - el.clientHeight < AT_BOTTOM_THRESHOLD_PX;
      atBottomRef.current = isAtBottom;
      setAtBottom(isAtBottom);
      if (isAtBottom) setNewWhileAway(0);

      if (el.scrollTop < LOAD_MORE_THRESHOLD_PX && hasMore && !loadingMoreRef.current) {
        loadingMoreRef.current = true;
        prevScrollHeightRef.current = el.scrollHeight;
        try {
          await loadMore();
        } finally {
          loadingMoreRef.current = false;
        }
      }
    },
    [hasMore, loadMore]
  );

  // Search jump: scroll to the target when it's in the rendered set
  useEffect(() => {
    if (!scrollToMessageId) return;
    const el = scrollerRef.current?.querySelector(
      `[data-message-id="${scrollToMessageId}"]`
    );
    if (el) {
      (el as HTMLElement).scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  }, [scrollToMessageId, items]);

  return (
    <div className="relative min-h-0 flex-1">
      <div ref={scrollerRef} onScroll={handleScroll} className="h-full overflow-y-auto">
        <div className="py-3 text-center text-xs text-flock-navy-300">
          {hasMore ? 'Scroll up for older messages' : 'This is the beginning of the conversation'}
        </div>

        {items.map((item) => {
          if (item.kind === 'day') {
            return (
              <div key={item.key} className="flex items-center gap-3 px-6 py-3">
                <div className="h-px flex-1 bg-flock-gray-200" />
                <span className="text-xs font-medium text-flock-navy-400">{item.label}</span>
                <div className="h-px flex-1 bg-flock-gray-200" />
              </div>
            );
          }
          if (item.kind === 'unread') {
            return (
              <div key={item.key} data-unread-divider className="flex items-center gap-3 px-6 py-2">
                <div className="h-px flex-1 bg-red-300" />
                <span className="text-xs font-semibold text-red-500">New messages</span>
                <div className="h-px flex-1 bg-red-300" />
              </div>
            );
          }
          return (
            <MessageRow
              key={item.key}
              message={item.message}
              own={item.message.userId === uid}
              grouped={item.grouped}
              onReplyClick={onReplyClick}
              onToggleReaction={onToggleReaction}
              renderRichContent={renderRichContent}
              onOpenActions={onOpenActions}
              resolveAvatar={resolveAvatar}
              highlighted={scrollToMessageId === item.message.id}
            />
          );
        })}

        <div className="h-3" />
      </div>

      {!atBottom && (
        <button
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-4 right-4 flex items-center gap-1.5 rounded-full bg-flock-navy-700 px-3 py-2 text-xs font-medium text-white shadow-lg transition hover:bg-flock-navy-800"
        >
          {newWhileAway > 0 && <span>{newWhileAway} new</span>}
          <ChevronDownIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
